import { onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'

interface UseIdleLogoutOptions {
  idleMinutes?: number
  warningSeconds?: number // seconds before logout to show warning modal
}

/**
 * Logs the user out and returns to home after a period of inactivity.
 * Activity is reset on common user interactions.
 */
export function useIdleLogout(options: UseIdleLogoutOptions = {}) {
  const authStore = useAuthStore()

  const defaultMinutes = Number(import.meta.env.VITE_SESSION_IDLE_MINUTES) || 30
  const idleMinutes = options.idleMinutes ?? defaultMinutes
  const warningSeconds = options.warningSeconds ?? 90

  let lastActivity = Date.now()
  let intervalHandle: number | undefined
  let warningShown = false

  const activityEvents = [
    'mousemove',
    'mousedown',
    'keydown',
    'touchstart',
    'scroll',
    'click'
  ] as const

  const markActivity = () => {
    const now = Date.now()
    const timeSinceLastActivity = now - lastActivity
    
    // Only log if there was a significant gap (more than 10 seconds)
    if (timeSinceLastActivity > 10000) {
      console.log(`🖱️ User activity detected after ${Math.floor(timeSinceLastActivity / 1000)} seconds`)
    }
    
    lastActivity = now
    if (warningShown) {
      console.log('✅ Idle warning dismissed by user activity')
      // notify listeners to hide modal
      window.dispatchEvent(new CustomEvent('teamio-idle-hide'))
      warningShown = false
    }
  }

  const start = () => {
    activityEvents.forEach(ev => window.addEventListener(ev, markActivity, { passive: true }))

    console.log(`🔐 Idle logout initialized: ${idleMinutes} minutes timeout, ${warningSeconds} seconds warning`)

    // check every 5s for precision near the end and low cost elsewhere
    intervalHandle = window.setInterval(async () => {
      if (!authStore.isAuthenticated) {
        console.log('⏭️ Idle check skipped: user not authenticated')
        return
      }
      const elapsedMs = Date.now() - lastActivity
      const limitMs = idleMinutes * 60 * 1000
      const remainingMs = limitMs - elapsedMs

      // Log status every minute
      if (Math.floor(elapsedMs / 60000) !== Math.floor((elapsedMs - 5000) / 60000)) {
        console.log(`⏱️ Idle status: ${Math.floor(elapsedMs / 60000)} minutes idle (limit: ${idleMinutes} minutes)`)
      }

      // Show pre-timeout warning once
      if (!warningShown && remainingMs <= warningSeconds * 1000 && remainingMs > 0) {
        console.log(`⚠️ Showing idle warning: ${Math.floor(remainingMs / 1000)} seconds remaining`)
        warningShown = true
        window.dispatchEvent(new CustomEvent('teamio-idle-warning', {
          detail: {
            remainingSeconds: Math.max(1, Math.floor(remainingMs / 1000)),
            totalSeconds: idleMinutes * 60
          }
        }))
      }

      if (remainingMs <= 0) {
        console.log('⏱️ Idle timeout reached, signing out...')
        
        // Stop the interval immediately to prevent multiple triggers
        stop()
        
        // Clear session SYNCHRONOUSLY first - this is critical and sets isLoggingOut flag
        authStore.clearUserSession()
        console.log('✅ Idle logout - local state cleared')
        
        // Sign out from Supabase in background (don't wait)
        authStore.signOut().catch(e => {
          console.warn('⚠️ Idle logout Supabase error (will redirect anyway):', e)
        })
        
        // Force IMMEDIATE hard redirect with full URL to bypass all router guards and Vue lifecycle
        console.log('🔄 Idle logout - forcing IMMEDIATE redirect to login...')
        
        // Use the origin + path for absolute certainty
        const loginUrl = window.location.origin + '/login'
        console.log('🔄 Redirecting to:', loginUrl)
        
        // Use href for maximum compatibility - this WILL reload the page
        window.location.href = loginUrl
      }
    }, 5 * 1000)
  }

  const stop = () => {
    activityEvents.forEach(ev => window.removeEventListener(ev, markActivity))
    if (intervalHandle) {
      clearInterval(intervalHandle)
      intervalHandle = undefined
    }
  }

  onMounted(() => {
    // allow other tabs or the modal to reset timer
    window.addEventListener('teamio-idle-stay', markActivity)
    start()
  })
  onUnmounted(() => stop())

  return { start, stop }
}


